import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from './data/supabaseClient';
import { useTranslation } from 'react-i18next';
import { useToast } from './hooks/ToastContext';
import Spinner from './common/Spinner';
import { FaLock, FaCheck, FaArrowLeft } from 'react-icons/fa';
import { CustomButton2, CustomButton8, InputPassword1 } from './Templates';
import './ResetPassword.css';

/**
 * Password Reset Component
 * Handles password reset flow for users who clicked the reset link in their email
 */
const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation('common');
  const { addToast } = useToast();
  
  // Form state
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(true);
  const [isValidReset, setIsValidReset] = useState(false);
  
  // Password validation state
  const [passwordValidation, setPasswordValidation] = useState({
    minLength: false,
    hasUpperCase: false,
    hasLowerCase: false,
    hasNumber: false,
    passwordsMatch: false
  });

  // Check if user arrived via valid reset link
  useEffect(() => {
    const checkResetValidity = async () => {
      try {
        setIsVerifying(true);
        
        console.log('🔍 Reset page loaded:', {
          fullUrl: window.location.href,
          hash: window.location.hash,
          search: window.location.search
        });
        
        // First, handle the hash-based tokens (Supabase v2 style)
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const accessToken = hashParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token');
        const type = hashParams.get('type');
        
        console.log('📝 Hash params:', {
          hasAccessToken: !!accessToken,
          hasRefreshToken: !!refreshToken,
          type
        });
        
        // If we have tokens in the hash, set the session immediately
        if (accessToken && refreshToken && type === 'recovery') {
          console.log('✅ Found recovery tokens in hash - setting session');
          
          try {
            const { data: sessionData, error: sessionError } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken
            });
            
            if (sessionError) {
              console.error('❌ Session setting error:', sessionError);
              throw sessionError;
            }
            
            console.log('✅ Session set successfully:', {
              hasSession: !!sessionData.session,
              userEmail: sessionData.session?.user?.email
            });
            
            setIsValidReset(true);
            return;
          } catch (sessionError) {
            console.error('❌ Failed to set session:', sessionError);
            setIsValidReset(false);
            addToast({
              type: 'error',
              title: t('auth.invalid_reset_link'),
              description: sessionError.message || t('auth.reset_link_expired_or_invalid')
            });
            setTimeout(() => navigate('/login'), 3000);
            return;
          }
        }
        
        // Check for query parameters (older style or error cases)
        const tokenHash = searchParams.get('token_hash');
        const errorCode = searchParams.get('error_code');
        const errorDescription = searchParams.get('error_description');
        
        console.log('📝 Query params:', {
          hasTokenHash: !!tokenHash,
          errorCode,
          type: searchParams.get('type')
        });
        
        // Handle errors from Supabase
        if (errorCode) {
          console.error('❌ Supabase error:', errorCode, errorDescription);
          setIsValidReset(false);
          addToast({
            type: 'error',
            title: t('auth.invalid_reset_link'),
            description: errorDescription || t('auth.reset_link_expired_or_invalid')
          });
          setTimeout(() => navigate('/login'), 3000);
          return;
        }
        
        // Check current session (might already be set by Supabase)
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        console.log('📝 Current session check:', {
          hasSession: !!session,
          sessionError: sessionError?.message,
          userEmail: session?.user?.email
        });
        
        if (session && session.user) {
          console.log('✅ Valid session found');
          setIsValidReset(true);
          return;
        }
        
        // Try OTP verification if we have token_hash
        if (tokenHash && searchParams.get('type') === 'recovery') {
          console.log('🔄 Attempting OTP verification');
          
          try {
            const { data, error: otpError } = await supabase.auth.verifyOtp({
              token_hash: tokenHash,
              type: 'recovery'
            });
            
            if (otpError) {
              console.error('❌ OTP verification error:', otpError);
              throw otpError;
            }
            
            console.log('✅ OTP verified successfully');
            setIsValidReset(true);
            return;
          } catch (otpError) {
            console.error('❌ OTP verification failed:', otpError);
            setIsValidReset(false);
            addToast({
              type: 'error',
              title: t('auth.invalid_reset_link'),
              description: otpError.message || t('auth.reset_link_expired_or_invalid')
            });
            setTimeout(() => navigate('/login'), 3000);
            return;
          }
        }
        
        // No valid reset method found
        console.log('❌ No valid reset indicators found');
        setIsValidReset(false);
        addToast({
          type: 'error',
          title: t('auth.invalid_reset_link'),
          description: t('auth.please_request_new_reset')
        });
        setTimeout(() => navigate('/login'), 3000);
        
      } catch (error) {
        console.error('❌ Reset validation error:', error);
        setIsValidReset(false);
        addToast({
          type: 'error',
          title: t('errors.error_title'),
          description: error.message || t('auth.reset_validation_failed')
        });
        setTimeout(() => navigate('/login'), 3000);
      } finally {
        setIsVerifying(false);
      }
    };

    checkResetValidity();
  }, [searchParams, navigate, addToast, t]);

  // Validate password in real-time
  useEffect(() => {
    const validation = {
      minLength: newPassword.length >= 6,
      hasUpperCase: /[A-Z]/.test(newPassword),
      hasLowerCase: /[a-z]/.test(newPassword),
      hasNumber: /\d/.test(newPassword),
      passwordsMatch: newPassword === confirmPassword && newPassword.length > 0
    };
    setPasswordValidation(validation);
  }, [newPassword, confirmPassword]);

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    
    // Validate passwords
    if (newPassword.length < 6) {
      addToast({
        type: 'error',
        title: t('settings.error'),
        description: t('settings.password_length')
      });
      return;
    }
    
    if (newPassword !== confirmPassword) {
      addToast({
        type: 'error',
        title: t('settings.error'),
        description: t('settings.passwords_dont_match')
      });
      return;
    }
    
    try {
      setIsLoading(true);
      
      console.log('🔄 Attempting password reset...');
      
      // First, verify we still have a valid session
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !session) {
        console.error('❌ No valid session for password reset:', sessionError);
        throw new Error(t('auth.session_expired_please_retry'));
      }
      
      console.log('✅ Session verified, updating password...');
      
      // Update the user's password
      const { data, error } = await supabase.auth.updateUser({
        password: newPassword
      });
      
      if (error) {
        console.error('❌ Password update error:', error);
        throw error;
      }
      
      console.log('✅ Password updated successfully');
      
      // Success!
      addToast({
        type: 'success',
        title: t('auth.password_reset_success'),
        description: t('auth.password_updated_successfully')
      });
      
      // Clear the form
      setNewPassword('');
      setConfirmPassword('');
      
      // Sign out and redirect to login to ensure clean state
      setTimeout(async () => {
        await supabase.auth.signOut();
        navigate('/login', { 
          state: { 
            message: t('auth.password_reset_complete_login'),
            email: session?.user?.email 
          } 
        });
      }, 2000);
      
    } catch (error) {
      console.error('❌ Password reset error:', error);
      
      // Handle specific error cases
      let errorMessage = error.message;
      
      if (error.message?.includes('session')) {
        errorMessage = t('auth.session_expired_please_retry');
      } else if (error.message?.includes('weak')) {
        errorMessage = t('auth.password_too_weak');
      } else if (error.message?.includes('same')) {
        errorMessage = t('auth.password_same_as_current');
      }
      
      addToast({
        type: 'error',
        title: t('settings.error'),
        description: errorMessage || t('auth.password_reset_failed')
      });
      
      // If session expired, redirect back to login after delay
      if (error.message?.includes('session') || error.message?.includes('Auth')) {
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      }
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleBackToLogin = () => {
    navigate('/login');
  };

  // Show loading spinner while verifying
  if (isVerifying) {
    return (
      <div className="reset-password-container">
        <div className="reset-password-box">
          <Spinner size="medium" customText={t('auth.verifying_reset_link')} />
        </div>
      </div>
    );
  }

  // Show error state if not valid
  if (!isValidReset) {
    return (
      <div className="reset-password-container">
        <div className="reset-password-box error-state">
          <h2>{t('auth.invalid_reset_link')}</h2>
          <p>{t('auth.redirecting_to_login')}</p>
          <CustomButton2
            label={t('auth.back_to_login')}
            onClick={handleBackToLogin}
          />
        </div>
      </div>
    );
  }

  // Main reset form
  return (
    <div className="reset-password-container">
      <div className="reset-password-box">
        <div className="reset-password-header">
          <CustomButton8 onClick={handleBackToLogin} className="back-button">
            <FaArrowLeft />
          </CustomButton8>
          <h2>{t('auth.reset_your_password')}</h2>
          <p className="subtitle">{t('auth.enter_new_password_below')}</p>
        </div>

        <form onSubmit={handlePasswordReset} className="reset-password-form">
          <div className="form-group">
            <label htmlFor="newPassword">
              <FaLock className="field-icon" />
              {t('settings.new_password')}
            </label>
            <InputPassword1
              id="newPassword"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder={t('settings.new_password_placeholder')}
              disabled={isLoading}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">
              <FaLock className="field-icon" />
              {t('settings.confirm_password')}
            </label>
            <InputPassword1
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder={t('settings.confirm_password_placeholder')}
              disabled={isLoading}
              required
            />
          </div>

          {/* Password strength indicator */}
          <div className="password-strength">
            <p className="strength-title">{t('auth.password_requirements')}:</p>
            <ul className="strength-list">
              <li className={passwordValidation.minLength ? 'valid' : 'invalid'}>
                {passwordValidation.minLength ? <FaCheck /> : '○'}
                {t('auth.min_6_characters')}
              </li>
              <li className={passwordValidation.hasUpperCase ? 'valid' : 'invalid'}>
                {passwordValidation.hasUpperCase ? <FaCheck /> : '○'}
                {t('auth.one_uppercase_letter')}
              </li>
              <li className={passwordValidation.hasLowerCase ? 'valid' : 'invalid'}>
                {passwordValidation.hasLowerCase ? <FaCheck /> : '○'}
                {t('auth.one_lowercase_letter')}
              </li>
              <li className={passwordValidation.hasNumber ? 'valid' : 'invalid'}>
                {passwordValidation.hasNumber ? <FaCheck /> : '○'}
                {t('auth.one_number')}
              </li>
              <li className={passwordValidation.passwordsMatch && newPassword ? 'valid' : 'invalid'}>
                {passwordValidation.passwordsMatch && newPassword ? <FaCheck /> : '○'}
                {t('auth.passwords_match')}
              </li>
            </ul>
          </div>

          <div className="form-actions">
            <CustomButton2
              type="submit"
              label={isLoading ? t('settings.saving') : t('auth.reset_password_button')}
              disabled={isLoading || !passwordValidation.minLength || !passwordValidation.passwordsMatch}
              className="reset-button"
            >
              {isLoading && <Spinner size="small" showText={false} thickness={2} color="var(--BackG-color)" />}
            </CustomButton2>
          </div>
        </form>

        <div className="reset-footer">
          <p>{t('auth.remember_password')}</p>
          <button 
            type="button"
            className="link-button"
            onClick={handleBackToLogin}
            disabled={isLoading}
          >
            {t('auth.back_to_login')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;